import React, { useEffect, useState } from "react";

import { Heading, HeadingSizeOption } from "~/components/elements/typography/Heading";
import { Section } from "~/components/elements/Section";

import { WpPage_Page_FlexibleContent_ContactForm } from "~/_generated/types";
import clsx from "clsx";

// HubSpot embedded form. Replaces the previous custom Formik form.
// Uses the v2 programmatic embed (hbspt.forms.create) so the form renders on
// every mount — the v4 auto-scan embed only rendered on a full page reload,
// not on client-side (SPA) navigation.
const HUBSPOT_PORTAL_ID = "148371277";
const HUBSPOT_FORM_ID = "4e7f2b51-67d1-4433-ae67-30443735e626";
const HUBSPOT_REGION = "eu1"; // this portal lives on the EU region
const HUBSPOT_SCRIPT_SRC = "https://js-eu1.hsforms.net/forms/embed/v2.js";
const HUBSPOT_TARGET_ID = "hubspot-contact-form";

export default function ContactForm({
	section,
	heading,
	onSubmitSuccess,
	containerClasses,
	disableHeadingMargin,
	headingClasses,
}: WpPage_Page_FlexibleContent_ContactForm) {
	const [submitted, setIsSubmitted] = useState(false);

	return (
		<Section {...section}>
			<div className={clsx("container", containerClasses)}>
				<div className="mx-auto text-center">
					{/* Heading */}
					<Heading classes={headingClasses} as="h3" size={HeadingSizeOption.h3} text={submitted ? "Thank you for getting in touch! " : heading} />

					{/* Form */}
					<div className={clsx(!disableHeadingMargin && "mt-10 md:mt-[60px]", "mx-auto max-w-[588px]")}>
						{!submitted ? (
							<ContForm setIsSubmitted={setIsSubmitted} onSubmitSuccess={onSubmitSuccess} />
						) : (
							<div>
								<div>Someone will be in contact shortly</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</Section>
	);
}

export const ContForm = ({ setIsSubmitted, onSubmitSuccess }: { setIsSubmitted: (value: boolean) => void; onSubmitSuccess?: () => void }) => {
	useEffect(() => {
		let cancelled = false;

		const createForm = () => {
			if (cancelled) return;
			const hbspt = (window as any).hbspt;
			if (!hbspt?.forms) return;

			// Clear any prior render (e.g. SPA remount) before re-creating.
			const target = document.getElementById(HUBSPOT_TARGET_ID);
			if (target) target.innerHTML = "";

			hbspt.forms.create({
				region: HUBSPOT_REGION,
				portalId: HUBSPOT_PORTAL_ID,
				formId: HUBSPOT_FORM_ID,
				target: `#${HUBSPOT_TARGET_ID}`,
				onFormSubmitted: () => {
					setIsSubmitted(true);
					onSubmitSuccess?.();
				},
			});
		};

		if ((window as any).hbspt?.forms) {
			// Script already loaded (client-side navigation / remount) — render now.
			createForm();
		} else {
			// First load — inject the embed script once, then render on load.
			let script = document.querySelector<HTMLScriptElement>(`script[src="${HUBSPOT_SCRIPT_SRC}"]`);
			if (!script) {
				script = document.createElement("script");
				script.src = HUBSPOT_SCRIPT_SRC;
				document.body.appendChild(script);
			}
			script.addEventListener("load", createForm);
		}

		return () => {
			cancelled = true;
		};
	}, [setIsSubmitted, onSubmitSuccess]);

	return <div className="mt-9 text-left" id={HUBSPOT_TARGET_ID} />;
};
